"use client";

import { Menu } from "lucide-react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import NavLink from "../NavLink";
import PhoneLink from "@/components/common/links/PhoneLink";
import Icon from "@/components/Icons/Icon";
import IconPhone from "@/components/Icons/IconPhone";
import IconPlus from "@/components/Icons/IconPlus";
import { useAuth } from "@/contexts/AuthContext";
import { Link, usePathname } from "@/i18n/routing";
import { mobileNavigationLinks, phoneNumber, socialMediaIcons } from "@/utils/constants";
import { filterNavigationLinks } from "@/utils/navigation";

const NavMobileMenu = () => {
  const pathname = usePathname();
  const locale = useLocale();
  const tNav = useTranslations("Navigation");
  const tFooter = useTranslations("Footer");
  const { agentAccess, isAuthenticated, isAuthLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const visibleLinks = useMemo(
    () =>
      filterNavigationLinks(mobileNavigationLinks, {
        isSalesAgent: agentAccess.isSalesAgent,
        variant: "mobile",
      }),
    [agentAccess.isSalesAgent],
  );

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  const logoSrc = locale === "ar" ? "/assets/images/albacars-ar-logo.webp" : "/assets/images/albacars-logo.png";

  useEffect(() => {
    const preventPageScroll = (openModalState: boolean) => {
      if (openModalState) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    };

    preventPageScroll(isSidebarOpen);

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  return (
    <>
      <div className="flex space-x-2 xl:hidden">
        <button onClick={toggleSidebar}>
          <Icon
            icon={<Menu color="#4F5E71" />}
            width={14}
            height={14}
            className="bg-blue hover:cursor-pointer hover:bg-[#345A8C33]"
          />
        </button>
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 xl:hidden"
          onClick={closeSidebar}
          onKeyDown={(e) => e.key === "Enter" && closeSidebar()}
          role="button"
          tabIndex={0}
        />
      )}

      <div
        className={`no-scrollbar fixed inset-0 z-[99999] flex h-dvh w-dvw flex-col overflow-y-scroll bg-white shadow-lg ${
          isSidebarOpen ? "translate-x-0 rtl:translate-x-2" : locale === "ar" ? "translate-x-full" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out xl:hidden`}
      >
        <div className="flex items-center justify-between p-5">
          <div className="relative w-32">
            <Link href="/" onClick={closeSidebar}>
              <Image
                src={logoSrc}
                alt="logo"
                width={729}
                height={223}
                className="object-cover object-center"
                sizes="140px"
              />
            </Link>
          </div>
          <button onClick={closeSidebar} className="rounded-2xl bg-blue p-4 text-xl hover:bg-[#345A8C33]">
            <IconPlus className="rotate-45" />
          </button>
        </div>

        <div className="flex flex-1 flex-col justify-center space-y-3 px-5">
          {visibleLinks.map((link, idx) => {
            const href =
              link.name === "myFavorites" && isAuthenticated
                ? `${link.href}&authenticated=true`
                : link.href;

            return (
              <NavLink
                key={`mob-${link.href}-${idx}`}
                variant="navbar"
                href={href}
                isActive={pathname === link.href || pathname.startsWith(`${link.href}/`) || pathname === "/favorites"}
                onClick={closeSidebar}
                className="p-4 !text-base font-medium"
                noPrefetch={link?.noPrefetch ?? false}
              >
                {link.name === "clientPortal" && !isAuthenticated && !isAuthLoading
                  ? tNav("clientPortalAgentLogin")
                  : tNav(link.name)}
              </NavLink>
            );
          })}
        </div>

        <div className="mt-auto p-5">
          <div className="mx-auto rounded-2xl bg-blue p-4">
            <div>
              <h6 className="mb-2 font-inter text-sm font-medium text-[#292D3480] text-opacity-50">
                {tFooter("contacts")}
              </h6>
              <div className="flex items-center" dir="ltr">
                <PhoneLink
                  phoneNumber={phoneNumber}
                  linkClassNames="flex items-center hover:underline"
                  buttonClassNames="flex items-center hover:underline"
                >
                  <IconPhone />
                  <span className="ml-2 font-inter text-base">{phoneNumber}</span>
                </PhoneLink>
              </div>
            </div>
            <div className="mt-5">
              <h6 className="mb-2 font-inter text-sm font-medium text-[#292D3480] text-opacity-50">
                {tFooter("socialNetworks")}
              </h6>

              <div className="flex max-w-80 flex-wrap gap-3">
                {socialMediaIcons.map((iconItem) => (
                  <Link href={iconItem.url} target="_blank" key={iconItem.key} onClick={closeSidebar}>
                    <Icon
                      icon={<iconItem.default />}
                      width={10}
                      height={10}
                      className="cursor-pointer rounded-full bg-black invert"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavMobileMenu;
